/**
 * Action Types and Creators
 * Defines all state changes as explicit actions
 * Works with enhanced AppState to provide Redux-like patterns
 * 
 * @module store/actions
 */

/**
 * Action Types - Use constants to avoid typos
 * Organized by domain
 */
export const ActionTypes = {
  // App Lifecycle
  APP_INITIALIZED: 'APP_INITIALIZED',
  APP_ERROR: 'APP_ERROR',
  APP_RESET: 'APP_RESET',
  
  // UI State
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  SET_LOADING: 'SET_LOADING',
  SHOW_MODAL: 'SHOW_MODAL',
  HIDE_MODAL: 'HIDE_MODAL',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_THEME: 'SET_THEME',
  SHOW_TOAST: 'SHOW_TOAST',
  HIDE_TOAST: 'HIDE_TOAST',
  
  // Initiatives
  ADD_INITIATIVE: 'ADD_INITIATIVE',
  UPDATE_INITIATIVE: 'UPDATE_INITIATIVE',
  DELETE_INITIATIVE: 'DELETE_INITIATIVE',
  SET_INITIATIVES: 'SET_INITIATIVES',
  SELECT_INITIATIVE: 'SELECT_INITIATIVE',
  CLEAR_SELECTED_INITIATIVE: 'CLEAR_SELECTED_INITIATIVE',
  
  // Chat
  ADD_CHAT_MESSAGE: 'ADD_CHAT_MESSAGE',
  CLEAR_CHAT: 'CLEAR_CHAT',
  SET_TYPING: 'SET_TYPING',
  UPDATE_CONVERSATION_CONTEXT: 'UPDATE_CONVERSATION_CONTEXT',
  SET_CONVERSATION_DEPTH: 'SET_CONVERSATION_DEPTH',
  
  // Settings & User
  SET_API_KEY: 'SET_API_KEY',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  SET_MODEL: 'SET_MODEL',
  
  // Strategy
  SET_UPLOADED_STRATEGY: 'SET_UPLOADED_STRATEGY',
  SET_EMERGENT_STRATEGY: 'SET_EMERGENT_STRATEGY',
  SET_CURRENT_STRATEGY: 'SET_CURRENT_STRATEGY',
  CLEAR_STRATEGIES: 'CLEAR_STRATEGIES',
  
  // Portfolio
  SET_PORTFOLIO_ANALYSIS: 'SET_PORTFOLIO_ANALYSIS',
  SET_PORTFOLIO_CONFLICTS: 'SET_PORTFOLIO_CONFLICTS',
  UPDATE_PORTFOLIO_STATUS: 'UPDATE_PORTFOLIO_STATUS',
  
  // System
  ADD_ERROR: 'ADD_ERROR',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_VERSION: 'SET_VERSION',
  SET_INITIALIZED: 'SET_INITIALIZED'
};

/**
 * Action Creators
 * Functions that return properly formatted actions
 */
export const actions = {
  // ============= UI Actions =============
  setCurrentTab: (tabId) => ({
    type: ActionTypes.SET_CURRENT_TAB,
    payload: tabId,
    meta: { timestamp: Date.now() }
  }),

  setLoading: (isLoading, message = null) => ({
    type: ActionTypes.SET_LOADING,
    payload: { isLoading, message },
    meta: { timestamp: Date.now() }
  }),

  showModal: (modalName, props = {}) => ({
    type: ActionTypes.SHOW_MODAL,
    payload: { modalName, props },
    meta: { timestamp: Date.now() }
  }),

  hideModal: () => ({
    type: ActionTypes.HIDE_MODAL,
    meta: { timestamp: Date.now() }
  }),

  toggleSidebar: () => ({
    type: ActionTypes.TOGGLE_SIDEBAR,
    meta: { timestamp: Date.now() }
  }),

  setTheme: (theme) => ({
    type: ActionTypes.SET_THEME,
    payload: theme,
    meta: { timestamp: Date.now() }
  }),

  showToast: (message, type = 'info', duration = 3000) => ({
    type: ActionTypes.SHOW_TOAST,
    payload: { 
      id: Date.now(), 
      message, 
      type, 
      duration 
    },
    meta: { timestamp: Date.now() }
  }),

  // ============= Initiative Actions =============
  addInitiative: (initiative) => ({
    type: ActionTypes.ADD_INITIATIVE,
    payload: {
      ...initiative,
      id: initiative.id || Date.now(),
      createdAt: initiative.createdAt || new Date().toISOString(),
      status: initiative.status || 'draft'
    },
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  updateInitiative: (id, updates) => ({
    type: ActionTypes.UPDATE_INITIATIVE,
    payload: { id, updates, updatedAt: new Date().toISOString() },
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  deleteInitiative: (id) => ({
    type: ActionTypes.DELETE_INITIATIVE,
    payload: id,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setInitiatives: (initiatives) => ({
    type: ActionTypes.SET_INITIATIVES,
    payload: initiatives,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  selectInitiative: (id) => ({
    type: ActionTypes.SELECT_INITIATIVE,
    payload: id,
    meta: { timestamp: Date.now() }
  }),

  // ============= Chat Actions =============
  addChatMessage: (content, sender, transfer = null) => ({
    type: ActionTypes.ADD_CHAT_MESSAGE,
    payload: {
      id: Date.now() + Math.random(),
      content,
      sender,
      transfer,
      timestamp: new Date().toISOString()
    },
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  clearChat: () => ({
    type: ActionTypes.CLEAR_CHAT,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setTyping: (isTyping) => ({
    type: ActionTypes.SET_TYPING,
    payload: isTyping,
    meta: { timestamp: Date.now() }
  }),

  updateConversationContext: (updates) => ({
    type: ActionTypes.UPDATE_CONVERSATION_CONTEXT,
    payload: updates,
    meta: { timestamp: Date.now() }
  }),

  setConversationDepth: (depth) => ({
    type: ActionTypes.SET_CONVERSATION_DEPTH,
    payload: depth,
    meta: { timestamp: Date.now() }
  }),

  // ============= Settings Actions =============
  setApiKey: (apiKey) => ({
    type: ActionTypes.SET_API_KEY,
    payload: apiKey,
    meta: { 
      timestamp: Date.now(),
      persist: true,
      sensitive: true 
    }
  }),

  updateSettings: (settings) => ({
    type: ActionTypes.UPDATE_SETTINGS,
    payload: settings,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  updatePreferences: (preferences) => ({
    type: ActionTypes.UPDATE_PREFERENCES,
    payload: preferences,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setModel: (model) => ({
    type: ActionTypes.SET_MODEL,
    payload: model,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  // ============= Strategy Actions =============
  setUploadedStrategy: (strategy) => ({
    type: ActionTypes.SET_UPLOADED_STRATEGY,
    payload: strategy,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setEmergentStrategy: (strategy) => ({
    type: ActionTypes.SET_EMERGENT_STRATEGY,
    payload: strategy,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setCurrentStrategy: (strategy) => ({
    type: ActionTypes.SET_CURRENT_STRATEGY,
    payload: strategy,
    meta: { timestamp: Date.now() }
  }),

  clearStrategies: () => ({
    type: ActionTypes.CLEAR_STRATEGIES,
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  // ============= Portfolio Actions =============
  setPortfolioAnalysis: (analysis) => ({
    type: ActionTypes.SET_PORTFOLIO_ANALYSIS,
    payload: {
      ...analysis,
      analyzedAt: new Date().toISOString()
    },
    meta: { 
      timestamp: Date.now(),
      persist: true 
    }
  }),

  setPortfolioConflicts: (conflicts) => ({
    type: ActionTypes.SET_PORTFOLIO_CONFLICTS,
    payload: conflicts,
    meta: { timestamp: Date.now() }
  }),

  updatePortfolioStatus: (status) => ({
    type: ActionTypes.UPDATE_PORTFOLIO_STATUS,
    payload: status,
    meta: { timestamp: Date.now() }
  }),

  // ============= System Actions =============
  appInitialized: () => ({
    type: ActionTypes.APP_INITIALIZED,
    meta: { timestamp: Date.now() }
  }),

  appError: (error) => ({
    type: ActionTypes.APP_ERROR,
    payload: {
      message: error.message || 'Unknown error',
      stack: error.stack,
      code: error.code,
      timestamp: new Date().toISOString()
    },
    meta: { timestamp: Date.now() }
  }),

  appReset: (options = {}) => ({
    type: ActionTypes.APP_RESET,
    payload: options,
    meta: { timestamp: Date.now() }
  }),

  addError: (error) => ({
    type: ActionTypes.ADD_ERROR,
    payload: error,
    meta: { timestamp: Date.now() }
  }),

  clearErrors: () => ({
    type: ActionTypes.CLEAR_ERRORS,
    meta: { timestamp: Date.now() }
  })
};

/**
 * Async Action Creators (Thunks)
 * Return functions that can dispatch multiple actions
 */
export const asyncActions = {
  /**
   * Save initiative with loading states
   */
  saveInitiativeAsync: (initiative) => async (dispatch, getState) => {
    dispatch(actions.setLoading(true, 'Saving initiative...'));
    
    try {
      // Add to state
      const action = dispatch(actions.addInitiative(initiative));
      
      // Additional async operations could go here
      // e.g., API calls, analytics, etc.
      
      dispatch(actions.showToast('Initiative saved successfully', 'success'));
      return action.payload;
      
    } catch (error) {
      dispatch(actions.appError(error));
      dispatch(actions.showToast('Failed to save initiative', 'error'));
      throw error;
    } finally {
      dispatch(actions.setLoading(false));
    }
  },

  /**
   * Load all initiatives
   */
  loadInitiativesAsync: () => async (dispatch, getState) => {
    dispatch(actions.setLoading(true, 'Loading initiatives...'));
    
    try {
      // Load from centralized service/repo
      const svc = (await import('../services/InitiativeService.js')).default;
      if (!svc.initialized) { try { await svc.initialize(); } catch {} }
      const initiatives = svc.getInitiatives();
      dispatch(actions.setInitiatives(initiatives));
    } catch (error) {
      dispatch(actions.appError(error));
      console.error('Failed to load initiatives:', error);
    } finally {
      dispatch(actions.setLoading(false));
    }
  },

  /**
   * Delete initiative with confirmation
   */
  deleteInitiativeAsync: (id) => async (dispatch, getState) => {
    try {
      dispatch(actions.deleteInitiative(id));
      dispatch(actions.showToast('Initiative deleted', 'info'));
    } catch (error) {
      dispatch(actions.appError(error));
      dispatch(actions.showToast('Failed to delete initiative', 'error'));
    }
  },

  /**
   * Send chat message with AI response
   */
  sendChatMessageAsync: (message) => async (dispatch, getState) => {
    // Add user message
    dispatch(actions.addChatMessage(message, 'user'));
    dispatch(actions.setTyping(true));
    
    try {
      // This would call the AI service
      // const response = await apiService.call(message);
      // dispatch(actions.addChatMessage(response, 'ai'));
      
      // For now, just simulate
      setTimeout(() => {
        dispatch(actions.addChatMessage('AI response here', 'ai'));
        dispatch(actions.setTyping(false));
      }, 1000);
      
    } catch (error) {
      dispatch(actions.appError(error));
      dispatch(actions.addChatMessage('Error getting response', 'ai'));
      dispatch(actions.setTyping(false));
    }
  }
};

/**
 * Action validation helper
 */
export function isValidAction(action) {
  return action && 
         typeof action === 'object' && 
         typeof action.type === 'string' &&
         Object.values(ActionTypes).includes(action.type);
}

/**
 * Create action type with namespace
 */
export function createActionType(namespace, type) {
  return `${namespace}/${type}`;
}

/**
 * Batch multiple actions
 */
export function batchActions(actions) {
  return {
    type: 'BATCH_ACTIONS',
    payload: actions,
    meta: { timestamp: Date.now() }
  };
}

// Export for debugging
if (import.meta.env.DEV) {
  window.StoreActions = { ActionTypes, actions, asyncActions };
  console.log('📦 Store actions available at window.StoreActions');
}
